// Job Group export: the lines of the sheet finance gets, as plain data (pure: no Excel, no Supabase, tested in groupSheet.test.ts).
// Every number comes straight from the database's group_rows / group_destination_totals; nothing is added up here, so the file and the
// Dashboard cannot disagree. Rows with no destination are listed first, flagged, never merged into a destination or dropped.
// Run: node --test --experimental-strip-types src/lib/groupSheet.test.ts
import type { GroupRow, GroupTotals } from './groupRows.ts'

export type Cell = string | number | null
export type SheetLine =
  | { kind: 'title' | 'note' | 'warning' | 'blank' | 'header'; cells: Cell[] }
  | { kind: 'section' | 'row' | 'subtotal' | 'total'; cells: Cell[] }

export const SHEET_HEADER = ['Row', 'Airway Bill', 'Flight No.', 'Shipper', 'Consignee', 'Description', 'From', 'To', 'Cartons', 'PMC', 'Gross Wt (kg)', 'Chargeable Wt (kg)', 'CBM']

const rowCells = (r: GroupRow): Cell[] => [
  r.job_ref, r.awb, r.flight_no, r.shipper_name, r.consignee_name, r.description, r.from_code, r.to_code,
  r.cartons, r.pmc_count, r.gross_weight, r.chargeable_weight, r.cbm,
]
// label sits in the first column, the figures under their own columns
const totalCells = (label: string, t: GroupTotals): Cell[] =>
  [label, null, null, null, null, null, null, `${t.shipments} ${t.shipments === 1 ? 'shipment' : 'shipments'}`, t.cartons, t.pmc_count, t.gross_weight, t.chargeable_weight, t.cbm]

export function buildGroupSheet(meta: { groupRef: string; customerName: string | null; generatedAt: string; generatedBy: string | null },
  rows: GroupRow[], totals: GroupTotals[]): SheetLine[] {
  const live = rows.filter(r => !r.cancelled)
  const out: SheetLine[] = []
  out.push({ kind: 'title', cells: [`Group ${meta.groupRef}${meta.customerName ? ` - ${meta.customerName}` : ''}`] })
  out.push({ kind: 'note', cells: [`Generated ${meta.generatedAt}${meta.generatedBy ? ` by ${meta.generatedBy}` : ''}. Removed rows are not included. Chargeable weight is the figure typed on each shipment.`] })
  const unassigned = live.filter(r => !r.to_port_id)
  if (unassigned.length > 0) {
    out.push({ kind: 'warning', cells: [`WARNING: ${unassigned.length} ${unassigned.length === 1 ? 'shipment has' : 'shipments have'} no destination (To). They are listed first, counted in the month total, and in no destination subtotal.`] })
  }
  out.push({ kind: 'blank', cells: [] })
  out.push({ kind: 'header', cells: SHEET_HEADER })
  const unassignedTotal = totals.find(t => t.sort_key === 2)
  if (unassigned.length > 0) {
    out.push({ kind: 'section', cells: ['NO DESTINATION - fill in the To field'] })
    unassigned.forEach(r => out.push({ kind: 'row', cells: rowCells(r) }))
    if (unassignedTotal) out.push({ kind: 'subtotal', cells: totalCells('Subtotal: no destination', unassignedTotal) })
    out.push({ kind: 'blank', cells: [] })
  }
  for (const t of totals.filter(x => x.sort_key === 1)) {
    out.push({ kind: 'section', cells: [`To: ${t.dest_code} - ${t.dest_name}`] })
    live.filter(r => r.to_port_id === t.dest_port_id).forEach(r => out.push({ kind: 'row', cells: rowCells(r) }))
    out.push({ kind: 'subtotal', cells: totalCells(`Subtotal ${t.dest_code}`, t) })
    out.push({ kind: 'blank', cells: [] })
  }
  const grand = totals.find(t => t.sort_key === 3)
  if (grand) out.push({ kind: 'total', cells: totalCells('MONTH TOTAL', grand) })
  return out
}
